import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Headphones, Eye, Mic, Video, CheckCircle, Play, Pause, Volume2 } from 'lucide-react';
import { Card } from '../components/Card';
interface PoemCourseModuleProps {
  onBack: () => void;
  courseId?: string;
}
interface PoemLine {
  pinyin: string[];
  chars: string[];
}
interface CourseContent {
  title: string;
  author: string;
  icon: string;
  bgColor: string;
  lines: PoemLine[];
  translation: string;
}
const courseData: Record<string, CourseContent> = {
  yonge: {
    title: '咏鹅',
    author: '唐·骆宾王',
    icon: '🦢',
    bgColor: 'bg-blue-50',
    lines: [{
      pinyin: ['é', 'é', 'é'],
      chars: ['鹅', '鹅', '鹅']
    }, {
      pinyin: ['qū', 'xiàng', 'xiàng', 'tiān', 'gē'],
      chars: ['曲', '项', '向', '天', '歌']
    }, {
      pinyin: ['bái', 'máo', 'fú', 'lǜ', 'shuǐ'],
      chars: ['白', '毛', '浮', '绿', '水']
    }, {
      pinyin: ['hóng', 'zhǎng', 'bō', 'qīng', 'bō'],
      chars: ['红', '掌', '拨', '清', '波']
    }],
    translation: '白鹅啊白鹅，弯曲着脖子朝天唱歌。白色的羽毛漂浮在绿水上，红色的脚掌拨动着清澈的水波。'
  },
  jingyesi: {
    title: '静夜思',
    author: '唐·李白',
    icon: '🌙',
    bgColor: 'bg-purple-50',
    lines: [{
      pinyin: ['chuáng', 'qián', 'míng', 'yuè', 'guāng'],
      chars: ['床', '前', '明', '月', '光']
    }, {
      pinyin: ['yí', 'shì', 'dì', 'shàng', 'shuāng'],
      chars: ['疑', '是', '地', '上', '霜']
    }, {
      pinyin: ['jǔ', 'tóu', 'wàng', 'míng', 'yuè'],
      chars: ['举', '头', '望', '明', '月']
    }, {
      pinyin: ['dī', 'tóu', 'sī', 'gù', 'xiāng'],
      chars: ['低', '头', '思', '故', '乡']
    }],
    translation: '明亮的月光洒在床前，好像地上泛起了一层白霜。抬头望着天上的明月，低下头不禁思念起远方的故乡。'
  },
  chunxiao: {
    title: '春晓',
    author: '唐·孟浩然',
    icon: '🌸',
    bgColor: 'bg-green-50',
    lines: [{
      pinyin: ['chūn', 'mián', 'bù', 'jué', 'xiǎo'],
      chars: ['春', '眠', '不', '觉', '晓']
    }, {
      pinyin: ['chù', 'chù', 'wén', 'tí', 'niǎo'],
      chars: ['处', '处', '闻', '啼', '鸟']
    }, {
      pinyin: ['yè', 'lái', 'fēng', 'yǔ', 'shēng'],
      chars: ['夜', '来', '风', '雨', '声']
    }, {
      pinyin: ['huā', 'luò', 'zhī', 'duō', 'shǎo'],
      chars: ['花', '落', '知', '多', '少']
    }],
    translation: '春天睡眠真好，不知不觉天就亮了。到处都能听到鸟儿的叫声。昨夜风雨交加，不知道花儿被吹落了多少。'
  },
  minnong: {
    title: '悯农',
    author: '唐·李绅',
    icon: '🌾',
    bgColor: 'bg-yellow-50',
    lines: [{
      pinyin: ['chú', 'hé', 'rì', 'dāng', 'wǔ'],
      chars: ['锄', '禾', '日', '当', '午']
    }, {
      pinyin: ['hàn', 'dī', 'hé', 'xià', 'tǔ'],
      chars: ['汗', '滴', '禾', '下', '土']
    }, {
      pinyin: ['shuí', 'zhī', 'pán', 'zhōng', 'cān'],
      chars: ['谁', '知', '盘', '中', '餐']
    }, {
      pinyin: ['lì', 'lì', 'jiē', 'xīn', 'kǔ'],
      chars: ['粒', '粒', '皆', '辛', '苦']
    }],
    translation: '农民在正午的烈日下锄地，汗水滴落在禾苗下的泥土里。有谁知道碗里的饭，每一粒都是农民辛苦劳动的成果。'
  }
};
// Simple audio generator
function playPoemMelody(lineIndex: number) {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const frequencies = [523.25, 587.33, 659.25, 783.99, 880]; // C5 pentatonic
  frequencies.slice(0, 5).forEach((freq, i) => {
    setTimeout(() => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, audioContext.currentTime);
      osc.connect(gain);
      gain.connect(audioContext.destination);
      gain.gain.setValueAtTime(0.2, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 0.4);
    }, i * 250);
  });
}
export function PoemCourseModule({
  onBack,
  courseId = 'yonge'
}: PoemCourseModuleProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeLine, setActiveLine] = useState(-1);
  const [activeChar, setActiveChar] = useState(-1);
  const data = courseData[courseId] || courseData['yonge'];
  const steps = [{
    id: 1,
    title: '听范唱',
    icon: Headphones,
    desc: '聆听专业范唱'
  }, {
    id: 2,
    title: '看动画',
    icon: Eye,
    desc: '理解诗意'
  }, {
    id: 3,
    title: '学跟唱',
    icon: Mic,
    desc: '跟唱练习'
  }, {
    id: 4,
    title: '演一演',
    icon: Video,
    desc: '角色扮演'
  }];
  const handlePlayPoem = () => {
    if (isPlaying) return;
    setIsPlaying(true);
    let charIndex = 0;
    data.lines.forEach((line, lineIdx) => {
      line.chars.forEach((_, charIdx) => {
        setTimeout(() => {
          setActiveLine(lineIdx);
          setActiveChar(charIdx);
          playPoemMelody(lineIdx);
        }, charIndex * 400);
        charIndex++;
      });
    });
    setTimeout(() => {
      setIsPlaying(false);
      setActiveLine(-1);
      setActiveChar(-1);
    }, charIndex * 400 + 500);
  };
  const renderStepContent = () => {
    switch (step) {
      case 1:
        // Listen
        return <div className="space-y-6" data-id="element-557">
            <div className="text-center" data-id="element-558">
              <div className={`inline-block p-6 rounded-3xl ${data.bgColor} mb-4`} data-id="element-559">
                <span className="text-6xl" data-id="element-560">{data.icon}</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-800 mb-2" data-id="element-561">
                {data.title}
              </h2>
              <p className="text-slate-500" data-id="element-562">{data.author}</p>
            </div>

            <Card className={`p-8 ${data.bgColor}`} data-id="element-563">
              <div className="space-y-6" data-id="element-564">
                {data.lines.map((line, lineIdx) => <div key={lineIdx} className="flex justify-center space-x-4" data-id="element-565">
                    {line.chars.map((char, charIdx) => <div key={charIdx} className="flex flex-col items-center" data-id="element-566">
                        <span className="text-xs text-slate-400 mb-1" data-id="element-567">
                          {line.pinyin[charIdx]}
                        </span>
                        <motion.span animate={{
                    scale: activeLine === lineIdx && activeChar === charIdx ? 1.3 : 1,
                    color: activeLine === lineIdx && activeChar === charIdx ? '#3B82F6' : '#1E293B'
                  }} className="text-3xl md:text-4xl font-bold" data-id="element-568">
                          {char}
                        </motion.span>
                      </div>)}
                  </div>)}
              </div>
            </Card>

            <div className="flex justify-center" data-id="element-569">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={handlePlayPoem} disabled={isPlaying} className={`px-8 py-4 rounded-full font-bold text-lg shadow-lg flex items-center ${isPlaying ? 'bg-blue-400 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-570">
                {isPlaying ? <>
                    <Volume2 className="mr-2 animate-pulse" data-id="element-571" /> 正在播放...
                  </> : <>
                    <Play className="mr-2" fill="currentColor" data-id="element-572" /> 播放范唱
                  </>}
              </motion.button>
            </div>
          </div>;
      case 2:
        // Watch Animation
        return <div className="space-y-6" data-id="element-573">
            <Card className={`p-8 ${data.bgColor} min-h-[300px] flex flex-col items-center justify-center`} data-id="element-574">
              <motion.div animate={{
              y: [0, -10, 0],
              rotate: [0, 5, -5, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} className="text-9xl mb-6" data-id="element-575">
                {data.icon}
              </motion.div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4" data-id="element-576">
                诗意动画
              </h3>
              <p className="text-slate-600 text-center max-w-md leading-relaxed" data-id="element-577">
                {data.translation}
              </p>
            </Card>

            <Card className="p-6 bg-white" data-id="element-578">
              <h4 className="font-bold text-lg mb-3 text-slate-800" data-id="element-579">
                📖 诗文赏析
              </h4>
              <p className="text-slate-600 leading-relaxed" data-id="element-580">
                {data.translation}
              </p>
            </Card>
          </div>;
      case 3:
        // Sing Along
        return <div className="space-y-6" data-id="element-581">
            <div className="bg-green-50 p-4 rounded-xl text-center text-green-800 font-medium" data-id="element-582">
              🎤 请跟随音乐，大声朗读跟唱！
            </div>

            <Card className={`p-8 ${data.bgColor}`} data-id="element-583">
              <div className="space-y-8" data-id="element-584">
                {data.lines.map((line, lineIdx) => <motion.div key={lineIdx} whileHover={{
                scale: 1.02
              }} onClick={() => {
                setActiveLine(lineIdx);
                playPoemMelody(lineIdx);
                setTimeout(() => setActiveLine(-1), 1500);
              }} className={`flex justify-center space-x-4 p-4 rounded-xl cursor-pointer transition-colors ${activeLine === lineIdx ? 'bg-white shadow-lg' : 'hover:bg-white/50'}`} data-id="element-585">
                    {line.chars.map((char, charIdx) => <div key={charIdx} className="flex flex-col items-center" data-id="element-586">
                        <span className="text-sm text-slate-400 mb-1" data-id="element-587">
                          {line.pinyin[charIdx]}
                        </span>
                        <span className={`text-4xl font-bold ${activeLine === lineIdx ? 'text-blue-600' : 'text-slate-800'}`} data-id="element-588">
                          {char}
                        </span>
                      </div>)}
                  </motion.div>)}
              </div>
            </Card>

            <p className="text-center text-slate-500 text-sm" data-id="element-589">
              💡 点击每一行可以单独练习
            </p>
          </div>;
      case 4:
        // Perform
        return <div className="space-y-6" data-id="element-590">
            <div className="bg-purple-50 p-4 rounded-xl text-center text-purple-800 font-medium" data-id="element-591">
              🎭 角色扮演时间！想象自己是诗人，表演这首诗吧！
            </div>

            <Card className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 text-center" data-id="element-592">
              <div className="text-8xl mb-6" data-id="element-593">{data.icon}</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-2" data-id="element-594">
                我是小诗人
              </h3>
              <p className="text-slate-500 mb-6" data-id="element-595">
                准备好了吗？让我们一起表演《{data.title}》
              </p>

              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto" data-id="element-596">
                <button className="p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all" data-id="element-597">
                  <span className="text-3xl mb-2 block" data-id="element-598">📷</span>
                  <span className="font-bold text-slate-700" data-id="element-599">拍照留念</span>
                </button>
                <button className="p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all" data-id="element-600">
                  <span className="text-3xl mb-2 block" data-id="element-601">🎬</span>
                  <span className="font-bold text-slate-700" data-id="element-602">录制视频</span>
                </button>
              </div>
            </Card>

            <Card className="p-6 bg-white" data-id="element-603">
              <h4 className="font-bold text-lg mb-3" data-id="element-604">🏆 完成挑战</h4>
              <div className="flex items-center space-x-4" data-id="element-605">
                <div className="flex-1 bg-slate-100 rounded-full h-3" data-id="element-606">
                  <div className="bg-green-500 h-3 rounded-full" style={{
                  width: '100%'
                }} data-id="element-607"></div>
                </div>
                <span className="text-green-600 font-bold" data-id="element-608">4/4</span>
              </div>
            </Card>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full flex flex-col bg-white" data-id="element-609">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-610">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-611">
          <div className="flex items-center space-x-4" data-id="element-612">
            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-id="element-613">
              <ArrowLeft size={24} className="text-slate-600" data-id="element-614" />
            </button>
            <div data-id="element-615">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-616">{data.title}</h1>
              <p className="text-xs text-slate-500" data-id="element-617">{data.author}</p>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center space-x-1 md:space-x-4" data-id="element-618">
            {steps.map((s, idx) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isCompleted = step > s.id;
            return <Fragment key={s.id} data-id="element-619">
                  <button onClick={() => setStep(s.id as any)} className={`flex flex-col items-center p-2 rounded-lg transition-all ${isActive ? 'bg-blue-50' : ''}`} data-id="element-620">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-all ${isActive ? 'bg-blue-600 text-white shadow-lg' : isCompleted ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-621">
                      {isCompleted ? <CheckCircle size={20} data-id="element-622" /> : <Icon size={20} data-id="element-623" />}
                    </div>
                    <span className={`text-xs font-medium hidden md:block ${isActive ? 'text-blue-600' : 'text-slate-500'}`} data-id="element-624">
                      {s.title}
                    </span>
                  </button>
                  {idx < steps.length - 1 && <div className={`w-8 h-0.5 ${step > s.id ? 'bg-green-400' : 'bg-slate-200'}`} data-id="element-625"></div>}
                </Fragment>;
          })}
          </div>

          <div className="w-16" data-id="element-626"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-slate-50 p-4 md:p-8" data-id="element-627">
        <div className="max-w-3xl mx-auto" data-id="element-628">
          <AnimatePresence mode="wait" data-id="element-629">
            <motion.div key={step} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-630">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-slate-100 p-4" data-id="element-631">
        <div className="max-w-3xl mx-auto flex justify-between items-center" data-id="element-632">
          <button onClick={() => setStep(Math.max(1, step - 1) as any)} disabled={step === 1} className="px-6 py-2 rounded-full border border-slate-200 text-slate-600 font-medium disabled:opacity-50 hover:bg-slate-50 transition-colors" data-id="element-633">
            上一步
          </button>

          <div className="flex items-center space-x-1" data-id="element-634">
            {[1, 2, 3, 4].map(s => <div key={s} className={`w-2 h-2 rounded-full ${step >= s ? 'bg-blue-600' : 'bg-slate-200'}`} data-id="element-635"></div>)}
          </div>

          <button onClick={() => {
          if (step < 4) setStep(step + 1 as any);else onBack();
        }} className="px-8 py-2 rounded-full bg-blue-600 text-white font-bold shadow-md hover:bg-blue-700 transition-colors" data-id="element-636">
            {step === 4 ? '完成课程 🎉' : '下一步'}
          </button>
        </div>
      </div>
    </motion.div>;
}